/**
 * IANA zones grouped by their region prefix for a native `<select>` with one
 * `<optgroup>` per region (PB-5134, d28). The list comes from the runtime's own
 * ICU data, so it is whatever the browser can actually format with; any value
 * passed in `include` (the stored or detected zone) is kept even when the
 * runtime does not list it, so a saved choice is never silently dropped.
 */
export interface TimeZoneGroup {
  readonly label: string;
  readonly zones: readonly string[];
}

const OTHER = "Other";

let supported: readonly string[] | null = null;

function supportedTimeZones(): readonly string[] {
  if (supported) return supported;
  try {
    supported =
      typeof Intl.supportedValuesOf === "function"
        ? Intl.supportedValuesOf("timeZone")
        : [];
  } catch {
    supported = [];
  }
  return supported;
}

function regionOf(zone: string): string {
  const slash = zone.indexOf("/");
  return slash > 0 ? zone.slice(0, slash) : OTHER;
}

export function timeZoneGroups(
  include: readonly string[] = [],
): readonly TimeZoneGroup[] {
  const zones = new Set(supportedTimeZones());
  for (const zone of include) if (zone) zones.add(zone);
  const groups = new Map<string, string[]>();
  for (const zone of zones) {
    const region = regionOf(zone);
    const bucket = groups.get(region);
    if (bucket) bucket.push(zone);
    else groups.set(region, [zone]);
  }
  return [...groups.entries()]
    .sort(([a], [b]) =>
      a === OTHER ? 1 : b === OTHER ? -1 : a.localeCompare(b, "en"),
    )
    .map(([label, members]) => ({
      label,
      zones: [...members].sort((a, b) => a.localeCompare(b, "en")),
    }));
}

/** "Europe/Berlin" → "Berlin", "America/Argentina/Buenos_Aires" → "Argentina – Buenos Aires". */
export function timeZoneLabel(zone: string): string {
  const slash = zone.indexOf("/");
  const rest = slash > 0 ? zone.slice(slash + 1) : zone;
  return rest.split("/").join(" – ").replace(/_/g, " ");
}
